import { useEffect, useState, type FunctionComponent } from 'react';
import { useFlags, withLDConsumer, type LDClient } from 'launchdarkly-react-client-sdk';
import { useLocation } from 'react-router-dom';
import { trackPageLoad } from '@fff-web/fff-analytics';
import { refreshUserInfo, useUserContext } from '@fff-web/fff-utilities';
import AllErrorMessage from '../../components/AllErrorMessage';
import Layout from '../../components/Layout';
import { searchToObject } from '../../helpers';
import useLaunchDarkly from '../../hooks/useLaunchDarkly';
import { redirectToBaseSite } from '../../utils/redirectMethods';
import MainBanner from './MainBanner';
import PerksBanner from './PerksBanner';
import ReactivateTodayDisclaimer from './ReactivateTodayDisclaimer';
import * as styles from './ReactivateToday.module.scss';

type ReactivateTodayProps = {
  ldClient: LDClient;
};

// Eligibility is resolved from a *freshly fetched* subscription state (see the mount
// effect). Render for a confirmed logged-in CANCELED member, and stay put on a genuinely
// unresolved read (timeout, or a degraded response where a *subscription-relevant* upstream
// failed and left the state empty) so a canceled member isn't bounced during transient
// growth/BFF degradation. Every other state redirects to the base site, matching the pre-fix
// gate — logged-out, ACTIVE/EXPIRED, and the canonical non-box `null`/'' state (menu-data
// returns null WITHOUT a subscription-upstream error for club/social/refill members, so they
// must not see the reactivate flow).
type EligibilityStatus = 'checking' | 'eligible' | 'redirect';

// Fall through to the page if the (uncached) refetch is slow/unresponsive, so a hung
// BFF can't trap the member on the loader indefinitely.
const FRESH_STATE_TIMEOUT_MS = 8000;

// Only these menu-data upstreams feed the box subscription-state computation (growth =
// subscriptions, user = account + membership flags). A failure in one of them makes an
// empty state unresolvable; a hermes (avatar) / storefront (sale) outage does not, so an
// empty state alongside only those is a legitimate non-box member and must still redirect.
const SUBSCRIPTION_UPSTREAM_SOURCES = ['growth', 'user'];

export const ReactivateToday: FunctionComponent<ReactivateTodayProps> = ({ ldClient }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [showRetry, setShowRetry] = useState(false);
  const [status, setStatus] = useState<EligibilityStatus>('checking');
  const { userInfo } = useUserContext();
  useLaunchDarkly(ldClient, userInfo);
  const { chatbotAppliedLab: showChatbot } = useFlags();
  const location = useLocation();
  const { coupon_code: couponCode } = searchToObject(location.search);

  // `userInfo` from context is populated once at app bootstrap from a 5-minute
  // sessionStorage cache — and behind that, the menu-data BFF keeps its own per-user
  // cache. A member who cancels elsewhere (e.g. the maui cancel flow) doesn't touch
  // this app's client cache, and the BFF entry can lag too, so the pre-cancel state
  // gets read here and the member is wrongly redirected. `refreshUserInfo` busts both
  // layers and cold-reads the BFF before we decide, so the gate sees the real state.
  useEffect(() => {
    let active = true;
    let timeoutId: ReturnType<typeof setTimeout>;

    const timeout = new Promise<'timeout'>((resolve) => {
      timeoutId = setTimeout(() => resolve('timeout'), FRESH_STATE_TIMEOUT_MS);
    });

    // `refreshUserInfo` never rejects — `getUserInfo` swallows any fetch failure (auth or
    // not) into a logged-out `defaultUserInfo`. So a failed refresh resolves to a non-
    // CANCELED state and falls into the redirect branch, matching the pre-fix gate.
    Promise.race([refreshUserInfo(), timeout]).then((result) => {
      if (!active) return;
      clearTimeout(timeoutId);
      // Couldn't determine state within the timeout window — don't trap the member on the
      // loader; show the page (reactivating a non-canceled sub is a safe upstream no-op).
      if (result === 'timeout') {
        setStatus('eligible');
        return;
      }
      const subscriptionState = result.user?.subscriptionState;
      const isCanceledMember = result.loggedIn && subscriptionState === 'CANCELED';
      // A degraded read where a subscription-relevant upstream failed comes back logged-in
      // with an empty state — unresolved, not a confirmed non-box member, so keep it on the
      // page. A real non-box member has an empty state with no such error → redirects.
      const isUnresolvedDegraded =
        result.loggedIn &&
        !subscriptionState &&
        !!result.upstreamErrors?.some((e) => SUBSCRIPTION_UPSTREAM_SOURCES.includes(e.source));
      setStatus(isCanceledMember || isUnresolvedDegraded ? 'eligible' : 'redirect');
    });

    return () => {
      active = false;
      clearTimeout(timeoutId);
    };
  }, []);

  useEffect(() => {
    if (status === 'eligible') {
      trackPageLoad({
        properties: { coupon_code: couponCode },
      }).then();
    }
    if (status === 'redirect') {
      redirectToBaseSite();
    }
  }, [status]); // eslint-disable-line react-hooks/exhaustive-deps

  // 'checking' shows the loader while we refetch; 'redirect' briefly shows it while
  // the browser navigates away.
  if (status !== 'eligible') {
    return (
      <Layout isLoading pageTitle="Reactivate Today" hideChatWidget={!showChatbot}>
        <></>
      </Layout>
    );
  }

  return (
    <Layout
      isLoading={isLoading}
      pageTitle="Reactivate Today"
      showError={showRetry}
      ErrorComponent={() => (
        <AllErrorMessage subjectAction="reactivate" onButtonClick={() => setShowRetry(false)} />
      )}
      hideChatWidget={!showChatbot}
    >
      <MainBanner isLoading={isLoading} setIsLoading={setIsLoading} setShowRetry={setShowRetry} />
      <div className={styles.divider} data-testid="reactivate-today-divider" />
      <PerksBanner />
      <ReactivateTodayDisclaimer
        isLoading={isLoading}
        setIsLoading={setIsLoading}
        setShowRetry={setShowRetry}
      />
    </Layout>
  );
};

export default withLDConsumer()(ReactivateToday);
